import { useEffect, useRef, useState } from 'react';
import type { ChatMessage } from '@hypevault/shared-types';
import { TWITCH_BADGE_URL, BADGE_LABEL } from '../chatBadges';

/**
 * Multi-Chat (Ticker) — horizontal companion to MultiChatWidget.
 *
 * Flex row anchored to the right edge. New messages slide in from the
 * right and push older pills leftward; once total width exceeds the
 * container, the leftmost pills clip off behind a left-edge fade mask.
 * Best at thin 1920×~80 strip dimensions across the top or bottom of
 * the stream.
 *
 * Config (all optional):
 *   maxMessages: number                           (default 12)
 *   accentColor: string                           (default '#a78bfa')
 *   bgStyle: 'semi' | 'solid' | 'transparent'    (default 'transparent')
 *   fontSize: number                              (default 20)
 *   showBadges: boolean                           (default true)
 *   showPlatformIcon: boolean                     (default true)
 *   hideCommands: boolean                         (default true)
 *   maxMessageChars: number                       (default 80)
 */

const PLATFORM_COLOR: Record<string, string> = {
  twitch: '#9146ff',
  youtube: '#ff0000',
  kick: '#53fc18',
  hypevault: '#f655a6',
};

function backgroundFor(style: string): string {
  if (style === 'solid') return 'linear-gradient(90deg,#0a0818,#120e26)';
  if (style === 'transparent') return 'transparent';
  return 'linear-gradient(90deg, rgba(10,8,24,0.7), rgba(18,14,38,0.7))';
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

export function MultiChatHorizontalWidget({
  fields,
  latestChatMessage,
}: {
  fields: Record<string, unknown>;
  latestChatMessage: ChatMessage | null;
}) {
  const maxMessages = Number(fields.maxMessages ?? 12);
  const accent = String(fields.accentColor ?? '#a78bfa');
  const bgStyle = String(fields.bgStyle ?? 'transparent');
  const fontSize = Number(fields.fontSize ?? 20);
  const showBadges = (fields.showBadges as boolean | undefined) ?? true;
  const showPlatformIcon = (fields.showPlatformIcon as boolean | undefined) ?? true;
  const hideCommands = (fields.hideCommands as boolean | undefined) ?? true;
  const maxChars = Number(fields.maxMessageChars ?? 240);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const lastId = useRef<string | null>(null);

  useEffect(() => {
    if (!latestChatMessage || latestChatMessage.id === lastId.current) return;
    lastId.current = latestChatMessage.id;
    if (hideCommands && latestChatMessage.command) return;
    setMessages((prev) => [...prev, latestChatMessage].slice(-maxMessages));
  }, [latestChatMessage, maxMessages, hideCommands]);

  // Soft fade on the left edge so messages clipping off behind newer ones
  // don't pop — they fade into the background as they're pushed out.
  const fadeMask = 'linear-gradient(to right, transparent 0, black 80px, black 100%)';

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: backgroundFor(bgStyle),
        fontFamily: "'Inter', system-ui, sans-serif",
        color: 'white',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 10,
        padding: '0 14px',
        overflow: 'hidden',
        borderRadius: bgStyle === 'transparent' ? 0 : 10,
        whiteSpace: 'nowrap',
        WebkitMaskImage: fadeMask,
        maskImage: fadeMask,
      }}
    >
      {messages.map((m) => {
        const platformColor = PLATFORM_COLOR[m.platform] ?? accent;
        return (
          <div
            key={m.id}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 12px',
              borderRadius: 999,
              background: 'rgba(255,255,255,0.04)',
              border: `1px solid ${platformColor}33`,
              fontSize,
              lineHeight: 1.2,
              flexShrink: 0,
              animation: 'mch-slide 0.35s cubic-bezier(0.16,1,0.3,1) both',
            }}
          >
            {showPlatformIcon && (
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: platformColor,
                  boxShadow: `0 0 6px ${platformColor}aa`,
                  flexShrink: 0,
                }}
                title={m.platform}
              />
            )}
            {showBadges && m.badges.length > 0 && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 3,
                  flexShrink: 0,
                }}
              >
                {m.badges.map((b) => (
                  <img
                    key={b}
                    src={TWITCH_BADGE_URL[b]}
                    alt={BADGE_LABEL[b]}
                    title={BADGE_LABEL[b]}
                    width={fontSize}
                    height={fontSize}
                    style={{ display: 'block', borderRadius: 3 }}
                    loading="lazy"
                    decoding="async"
                  />
                ))}
              </span>
            )}
            <span
              style={{
                fontWeight: 700,
                color: platformColor,
                flexShrink: 0,
              }}
            >
              {m.viewerName}:
            </span>
            <span style={{ color: 'rgba(255,255,255,0.92)' }}>{truncate(m.message, maxChars)}</span>
          </div>
        );
      })}
      {messages.length === 0 && (
        <div
          style={{
            opacity: 0.3,
            fontSize: fontSize * 0.9,
            fontStyle: 'italic',
          }}
        >
          Waiting for chat…
        </div>
      )}
      <style>{`
        @keyframes mch-slide {
          from { opacity: 0; transform: translateX(36px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
